import qs from 'qs'
import request, { authOptions } from 'api/requestUtils'
import type { ReferralOfferTypes } from 'api/mappers/promoMap'
import { itemTypeToReferralOfferType, promotionMap, promotionMapV2 } from 'api/mappers/promoMap'
import type { Promotion, definitions, operations, paths } from '@luxuryescapes/contract-svc-promo'
import config from '../constants/config'
import { sortBy } from 'lib/array/arrayUtils'

interface ReferralRequestBody {
  code: string
  country_code: string
  brand: string
  cart_value: number
  item_types: Array<ReferralOfferTypes>
}

export type DiscountRequestOrderV2 = definitions['Discount Request Order']

export type DiscountRequest =
  paths['/api/promo/discount']['post']['requestBody']['content']['application/json']

export type DiscountResponse =
  paths['/api/promo/discount']['post']['responses']['200']['content']['application/json']['result']

export type DiscountError =
  | operations['calculateDiscount']['responses']['400']['content']['application/json']['result']

/**
 * getPromoV2 uses the passed in order and user information to calculate the promo amount (on a per-item basis)
 * @param code_name - The requested code name
 * @param order - The order being discounted
 * @param user - Details of the user making the request
 * @returns
 */

export type AppDiscountError = paths['/api/promo/discount']['post']['responses']['400']['content']['application/json']['result']['errors']

export type RestrictedPromoRequestData = { code: string, brand: string, email: string, category: string }

type MyVouchersResult =
  operations['listMyVouchersV2']['responses']['200']['content']['application/json']['result']
type MyVouchersRequest =
  operations['listMyVouchersV2']['requestBody']['content']['application/json']
export type AllVouchersParams = operations['listMyVouchers']['parameters']['query']
export type MyVouchersResponse = {
  vouchers: Array<PublicVoucher>
  voucherConditionResults: Array<App.VoucherConditionResult>
}

export async function getPromoV2(
  { codeName, voucherIds, order, userId, requestSource }: definitions['Discount Request'],
): Promise<App.Promotion> {
  return request.post<App.ApiResponse<DiscountResponse | DiscountError>, DiscountRequest>('/api/promo/discount',
    {
      codeName,
      voucherIds,
      order,
      userId,
      requestSource,
    }, { credentials: 'include' })
    .then((results) => {
      if (results.status !== 200 || Object.hasOwn(results.result, 'errors')) {
        throw new Error(results.message ?? 'Could not apply promo')
      }
      // TODO: can we avoid this cast?
      const promoResult = results.result as unknown as DiscountResponse
      return promotionMapV2(promoResult.promo)
    })
}

export type PublicVoucher = App.PublicVoucher

export type AppliedVoucher = definitions['VoucherApplied']

type AllVouchersResult =
  operations['listMyVouchers']['responses']['200']['content']['application/json']['result']

export async function getAllVouchers(params: AllVouchersParams): Promise<Array<PublicVoucher>> {
  const queryParams = qs.stringify(params)
  return request.get<App.ApiResponse<AllVouchersResult>>(
    `/api/promo/my-vouchers?${queryParams}`,
    { credentials: 'include' },
  ).then(({ result }) => result.vouchers)
}

export async function getMyVouchers(
  params: MyVouchersRequest,
): Promise<MyVouchersResponse> {
  return request.post<App.ApiResponse<MyVouchersResult>, MyVouchersRequest>(
    '/api/promo/my-vouchers',
    params,
    { credentials: 'include' },
  ).then(({ result }) => ({
    vouchers: sortBy(result.vouchers, (voucher) => voucher.voucherDisplayAmount, 'desc'),
    voucherConditionResults: result.voucherConditionResults,
  }))
}

export async function getCompPackagePromo(data: {
  offerId: string
  region: string
}) {
  const queryParams = qs.stringify({
    region: data.region,
    offer: data.offerId,
  })

  return request.get<App.ApiResponse<Promotion.Promotion>>(
    `/api/promo/comped-package/generate-code?${queryParams}`,
    { credentials: 'include' },
  ).then(({ result }) => promotionMap(result))
}

interface ServerGiftCard {
  id_gift_card: string
  gift_card_code: string
  added_by: string
  gift_card_value: number
  created_at: string
  updated_at: string
  expires_at: string
  redeemed_at: string
  redeemed_by: string
  cancelled_at: string
  fk_order_id: string
  currency: string
  gift_card_status: 'awaiting_payment' | 'created' | 'cancelled' | 'redeemed'
  brand: string
  email: string
  fk_item_id: string
  personalised: {
    method: 'print' | 'email'
    to?: string
    from?: string
    message?: string
    email_of_recipient?: string
    is_blank_message: boolean
  }
  product?: {
    name: string
    imageId: string
    imageUrl: string
    items: Array<string>
    type: string
  }
}

export function getGiftCard(code: string): Promise<App.GiftCard> {
  return request.get<App.ApiResponse<ServerGiftCard>>(`/api/gift-card/code/${code}`, { credentials: 'include' })
    .then(response => {
      const giftCard = response.result
      return {
        id: giftCard.id_gift_card,
        code: giftCard.gift_card_code,
        added_by: giftCard.added_by,
        value: giftCard.gift_card_value,
        expiresAt: giftCard.expires_at,
        redeemedAt: giftCard.redeemed_at,
        redeemedBy: giftCard.redeemed_by,
        cancelledAt: giftCard.cancelled_at,
        orderId: giftCard.fk_order_id,
        currency: giftCard.currency,
        status: giftCard.gift_card_status,
        brand: giftCard.brand,
        email: giftCard.email,
        itemId: giftCard.fk_item_id,
        personalised: {
          method: giftCard.personalised.method,
          to: giftCard.personalised.to,
          from: giftCard.personalised.from,
          message: giftCard.personalised.message,
          email_of_recipient: giftCard.personalised.email_of_recipient,
          is_blank_message: giftCard.personalised.is_blank_message,
        },
        product: giftCard.product ? {
          image: { id: giftCard.product.imageId, url: giftCard.product.imageUrl, title: giftCard.product.name },
          name: giftCard.product.name,
          items: giftCard.product.items,
          type: giftCard.product.type,
        } : undefined,
      } as App.GiftCard
    })
}
export function getUserGiftCards(): Promise<Array<App.GiftCard>> {
  return request.get<App.ApiResponse<{ total: number, giftCards: Array<ServerGiftCard> }>>('/api/gift-card/me', { credentials: 'include' })
    .then(response => {
      return response.result.giftCards.map(giftCard => ({
        id: giftCard.id_gift_card,
        code: giftCard.gift_card_code,
        added_by: giftCard.added_by,
        value: giftCard.gift_card_value,
        expiresAt: giftCard.expires_at,
        redeemedAt: giftCard.redeemed_at,
        redeemedBy: giftCard.redeemed_by,
        cancelledAt: giftCard.cancelled_at,
        orderId: giftCard.fk_order_id,
        currency: giftCard.currency,
        status: giftCard.gift_card_status,
        brand: giftCard.brand,
        email: giftCard.email,
        itemId: giftCard.fk_item_id,
        personalised: { method: 'email', is_blank_message: true },
      } as App.GiftCard))
    })
}
export function redeemGiftCard(code: string, pin?: string, region?: string) {
  const uri = `/api/gift-card/code/${code}/redeem?brand=${config.BRAND}&region=${region}`

  return request.patch<
    operations['/api/gift-card/code/{code}/redeem/patch']['responses']['200']['content']['application/json'],
    operations['/api/gift-card/code/{code}/redeem/patch']['requestBody']['content']['application/json']
  >(uri, pin ? { pin } : {}, { credentials: 'include' })
}

type ScanGiftCardRequest =
  paths['/api/gift-card/scan-image']['post']['requestBody']['content']['application/json']

type ScanGiftCardResult =
  paths['/api/gift-card/scan-image']['post']['responses']['200']['content']['application/json']['result']

export function detectGiftCardNumber(imageBase64: string) {
  return request.post<App.ApiResponse<ScanGiftCardResult>, ScanGiftCardRequest>(
    '/api/gift-card/scan-image',
    { imageBase64 },
    { credentials: 'include' },
  ).then(({ result }) => ({
    cardNumber: result.code,
    accessCode: result.pin,
  }))
}

interface GetReferralPromoProps {
  codeName: string
  regionCode: string
  brand: string
  cartValue: number
  itemTypes: Array<App.Checkout.AnyItemType>
}

export function getReferralPromo({ codeName, regionCode, brand, cartValue, itemTypes }: GetReferralPromoProps) {
  const uri = '/api/referral/promo'

  const body: ReferralRequestBody = {
    code: codeName,
    country_code: regionCode,
    brand,
    cart_value: cartValue,
    item_types: itemTypes.map((it) => itemTypeToReferralOfferType(it)),
  }

  return request.post<App.ApiResponse<Promotion.Promotion>, ReferralRequestBody>(uri, body, { credentials: 'include' })
    .then(({ result }) => promotionMap(result))
    .catch((err) => err)
}

export function getCodeByUserId(userId: string, accessToken?: string) {
  return request.get<App.ApiResponse<{ details: { referral_code: string }, meta_data: Record<string, string> }>>(`/api/referral/code/${userId}`, authOptions(accessToken))
}

export function getReferralDetails(referralCode: string) {
  return request.get(`/api/referral/${referralCode}`, { credentials: 'include' })
}

export function getReferralCodeValidation(referralCode: string) {
  return request.get(`/api/referral/public/${referralCode}`)
}

export function claimShareReward(referralCode: string, earnKey: string) {
  return request.post('/api/referral/share-reward', { referralCode, earnKey }, { excludeBrand: true })
}

export async function registerCorporateBenefits(code: string, corporateEmail: string) {
  return request.post<{ code: string, email: string }, { }>('/api/promo/corporate', { code, corporateEmail }, { credentials: 'include' })
}

export async function registerFriendsAndFamily(data: RestrictedPromoRequestData) {
  return request.post('/api/promo/request-restricted-promo', data, { credentials: 'include' })
}

type ChannelMarkup = paths['/api/promo/channel-markup']['get']['responses']['200']['content']['application/json']['result']

function mapChannelMarkup(serverChannelMarkup: ChannelMarkup): App.ChannelMarkup {
  return {
    channelMarkupId: serverChannelMarkup.channel_markup_id,
    channelMarkupValue: serverChannelMarkup.markup ?? 0,
    luxplusMarkupValue: serverChannelMarkup.luxplus_markup,
    channelMarkupBlacklist: serverChannelMarkup.channel_markup_blacklist,
  }
}

type GetPromoDisplayConfigResponse = paths['/api/promo/promo-display-config']['get']['responses']['200']['content']['application/json']['result']
function mapPromoDisplayConfig(serverPromoDisplayConfig: GetPromoDisplayConfigResponse, blacklistPromoDisplayIds: Array<string>): App.PromoDisplayConfigMap {
  const filteredPromoDisplayConfigs = serverPromoDisplayConfig.promoDisplayConfigs.filter(config => !blacklistPromoDisplayIds.includes(config.promo_display_config_id))
  const res: { [promoDisplayId: string]: App.PromoDisplayConfig } = {}
  filteredPromoDisplayConfigs.forEach((promoDisplayConfig) => {
    res[promoDisplayConfig.promo_display_config_id] = {
      headerText: promoDisplayConfig.header_text,
      bodyText: promoDisplayConfig.body_text,
      codeName: promoDisplayConfig.code_name,
      urlPath: promoDisplayConfig.url_path,
      promoDisplayId: promoDisplayConfig.promo_display_config_id,
      displayMode: promoDisplayConfig.display_mode,
      authState: promoDisplayConfig.auth_state,
      delayDuration: promoDisplayConfig.delay_duration || 0,
      displayOptionKey: '',
      needsOfferCheck: promoDisplayConfig.needsCheck ?? false,
      passOfferCheck: {},
    }
  })
  return res
}

export function getChannelMarkupByFilters(filters: App.ChannelMarkupFilters): Promise<App.ChannelMarkup> {
  const query = qs.stringify(filters)
  return request.get<App.ApiResponse<ChannelMarkup>>(
    `/api/promo/channel-markup?${query}`,
  ).then((response) => mapChannelMarkup(response.result))
}

export function getChannelMarkupById(id: string): Promise<App.ChannelMarkup> {
  return request.get<App.ApiResponse<ChannelMarkup>>(
    `/api/promo/channel-markup/${id}`,
  ).then(response => mapChannelMarkup(response.result))
}

type SendContentfulFormDataResponse = operations['submitContentfulFormEntry']['responses']['201']['content']['application/json']
export function sendContentfulFormData({ slug, data }: { slug: string, data: object }) {
  return request.post<App.ApiResponse<SendContentfulFormDataResponse>, { data: object }>(`/api/promo/contentful-forms/${slug}`, { data })
}

export type VoucherDisplayConfig = definitions['PublicVoucherDisplayConfig']
export type VoucherDisplayConfigVoucher = definitions['PublicVoucherDisplayConfigVoucher']

type GetVoucherDisplayConfigsParams = { region: string }
type GetVoucherDisplayConfigsResult = operations['getVoucherDisplayConfigs']['responses']['200']['content']['application/json']['result']

export async function getVoucherDisplayConfigs(params: GetVoucherDisplayConfigsParams): Promise<Array<VoucherDisplayConfig>> {
  const queryParams = qs.stringify(params)
  return request.get<App.ApiResponse<GetVoucherDisplayConfigsResult>>(
    `/api/promo/voucher-display-config?${queryParams}`,
    { credentials: 'include' },
  ).then(({ result }) => result.voucherDisplayConfigs)
}

type GetClaimedVoucherDisplayConfigVouchersParams = { region: string }
type GetClaimedVoucherDisplayConfigVouchersResult = operations['getClaimedVoucherDisplayConfigVouchers']['responses']['200']['content']['application/json']['result']

export async function getClaimedVoucherDisplayConfigVouchers(params: GetClaimedVoucherDisplayConfigVouchersParams, accessToken?: string): Promise<Array<string>> {
  const queryParams = qs.stringify(params)
  return request.get<App.ApiResponse<GetClaimedVoucherDisplayConfigVouchersResult>>(
    `/api/promo/voucher-display-config/claimed?${queryParams}`,
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : { credentials: 'include' },
  ).then(({ result }) => result.claimedVoucherIds)
}

type ClaimVoucherDisplayConfigResult = operations['claimVoucherDisplayConfig']['responses']['200']['content']['application/json']['result']
type ClaimVoucherDisplayConfigBody = operations['claimVoucherDisplayConfig']['requestBody']['content']['application/json']

export async function claimVoucherDisplayConfig(voucherDisplayConfigId: string, voucherId: string): Promise<ClaimVoucherDisplayConfigResult> {
  return request.post<App.ApiResponse<ClaimVoucherDisplayConfigResult>, ClaimVoucherDisplayConfigBody>(
    `/api/promo/voucher-display-config/${voucherDisplayConfigId}/claim`,
    { voucherId },
    { credentials: 'include', excludeBrand: true },
  ).then(({ result }) => result)
}

export function getPromoDisplayConfig(params: Omit<paths['/api/promo/promo-display-config']['get']['parameters']['query'], 'brand'>, blacklistPromoDisplayIds: Array<string>) {
  const query = qs.stringify(params)
  return request.get<App.ApiResponse<GetPromoDisplayConfigResponse>>(`/api/promo/promo-display-config?${query}`).then(response => mapPromoDisplayConfig(response.result, blacklistPromoDisplayIds))
}

type GetTargetedPromoDetailsResponse = paths['/api/promo/promo-display-config/{promoDisplayConfigId}']['get']['responses']['200']['content']['application/json']['result']
export function getTargetedPromoDetails(promoDisplayId: string) {
  return request.get<App.ApiResponse<GetTargetedPromoDetailsResponse>>(`/api/promo/promo-display-config/${promoDisplayId}`, { credentials: 'include' }).then(response => ({ ...response.result, promoDisplayId }))
}

type CheckPromoDisplayConfigResponse = paths['/api/promo/promo-display-config/{promoDisplayConfigId}/check-offer/{offerId}']['get']['responses']['200']['content']['application/json']['result']

export type CheckPromoDisplayQuery = paths['/api/promo/promo-display-config/{promoDisplayConfigId}/check-offer/{offerId}']['get']['parameters']['query']

export function getPromoDisplayConfigValidation(promoDisplayId: string, offerId: string, path: string, queryParams: CheckPromoDisplayQuery) {
  const queryStr = qs.stringify(queryParams)
  return request.get<App.ApiResponse<CheckPromoDisplayConfigResponse>>(`/api/promo/promo-display-config/${promoDisplayId}/check-offer/${offerId}${queryStr ? `?${queryStr}` : ''}`, { credentials: 'include', excludeBrand: false })
    .then(response => ({ ...response.result, promoDisplayId, path: path }))
}

type GetOfferBypassRegionFilterSettingParams = paths['/api/promo/offer-bypass-region-filter-setting']['get']['parameters']['query']
export type OfferBypassRegionFilterSettingResult = paths['/api/promo/offer-bypass-region-filter-setting']['get']['responses']['200']['content']['application/json']['result']

export function getOfferBypassRegionFilterSetting(params: GetOfferBypassRegionFilterSettingParams): Promise<OfferBypassRegionFilterSettingResult> {
  const query = qs.stringify(params)
  return request.get<App.ApiResponse<OfferBypassRegionFilterSettingResult>>(
    `/api/promo/offer-bypass-region-filter-setting?${query}`,
    { excludeBrand: true },
  ).then(({ result }) => result)
}
